/**
 * Единый источник данных резюме.
 *
 * Используется одновременно:
 *   - страницей /(резюме) в React (src/pages/Resume.jsx и компоненты);
 *   - генератором PDF-версии CV (scripts/generate-cv-pdf.js, Node).
 *
 * Поэтому файл написан в CommonJS: webpack умеет импортировать CJS из ESM,
 * а Node — просто require(). Никаких импортов из src/ здесь быть не должно,
 * кроме чистых утилит без React (utils/experience.js).
 */

const {totalExperienceMonths, formatTotalYears} = require('../utils/experience');

const PROFILE = {
    name: 'Vsevolod Kosarev',
    firstName: 'Vsevolod',
    lastName: 'Kosarev',
    title: 'Unity C# Developer',
    // Короткая строка «где я сейчас» для шапки сайта
    currentLine: 'Currently in Serbia @ Matryoshka Games',
    // Локация для PDF (ATS читает её как место проживания кандидата)
    location: 'Serbia',
    website: 'https://kosarev.app/',
    // summary задаётся ниже: в нём фигурирует стаж, а он считается из EXPERIENCES
};

/**
 * Контакты. `label` — как показываем на сайте, `text` — как печатаем в PDF
 * (осознанно в виде читаемого URL: так ссылка остаётся полезной и в тексте,
 * который вытаскивают ATS и поисковые краулеры).
 */
const CONTACTS = [
    {
        id: 'email',
        emoji: '📧',
        label: 'iamvkosarev@gmail.com',
        text: 'iamvkosarev@gmail.com',
        url: 'mailto:iamvkosarev@gmail.com',
    },
    {
        id: 'telegram',
        emoji: '💬',
        label: 'Telegram',
        text: 't.me/iamvkosarev',
        url: 'https://t.me/iamvkosarev',
    },
    {
        id: 'linkedin',
        emoji: '💼',
        label: 'LinkedIn',
        text: 'linkedin.com/in/iamvkosarev',
        url: 'https://www.linkedin.com/in/iamvkosarev/',
    },
    {
        id: 'github',
        emoji: '💻',
        label: 'GitHub',
        text: 'github.com/iamvkosarev',
        url: 'https://github.com/iamvkosarev',
    },
    {
        id: 'website',
        emoji: '🌐',
        label: 'kosarev.app',
        text: 'kosarev.app',
        url: 'https://kosarev.app/',
        // На самом сайте ссылка на сайт не нужна — только в PDF
        siteOnly: false,
        pdfOnly: true,
    },
];

const EXPERIENCES = [
    {
        company: 'Matryoshka Games',
        companyUrl: 'https://www.matryoshka.com/',
        position: 'Unity Developer',
        startDate: '2024-05',
        endDate: null,
        location: 'Serbia (on-site, May 2026)',
        projects: [
            {
                name: 'Royal Cooking',
                note: 'current',
                links: [
                    {
                        label: 'Android',
                        url: 'https://play.google.com/store/apps/details?id=com.matryoshka.royal.cooking.kitchen.madness',
                    },
                    {label: 'iOS', url: 'https://apps.apple.com/us/app/royal-cooking/id1664415775'},
                ],
            },
            {
                name: 'Cooking Craze',
                links: [
                    {
                        label: 'Android',
                        url: 'https://play.google.com/store/apps/details?id=com.bigfishgames.cookingcrazegooglef2p',
                    },
                    {
                        label: 'iOS',
                        url: 'https://apps.apple.com/us/app/cooking-craze-restaurant-game/id1029094059',
                    },
                ],
            },
        ],
        responsibilities: [
            'Writing multi-project sub-modules',
            'Implementing new game mechanics',
            'Writing documentation',
            'Updating SDKs for different platforms',
            'Upgrading Unity versions',
            'Porting to mobile, Desktop and console platforms',
            'Building Unity Editor tooling for other teams',
            'Helping other developers with debugging and performance optimization',
            'Working with CI/CD pipelines',
            'Code optimization',
        ],
        achievements: [
            'Adapted a portrait-only title to landscape — reworked every game scene and rebuilt the UI layout',
            'Added full input support for Desktop and consoles (gamepad and keyboard navigation)',
            'Ported and released the game on alternative stores: Huawei App Gallery, Xiaomi GetApps, vivo App Store, OPPO App Market, Samsung Galaxy Store and RuStore',
            'Migrated the Microsoft Store build from UWP to Windows Standalone',
            'Built Unity Editor tools adopted by several company teams — sped up prefab and scene setup',
            'Optimized CI/CD logging for better build status tracking',
            'Refactored and optimized a 9-year-old legacy project',
        ],
    },
    {
        company: 'MeTime Games',
        position: 'Unity Developer',
        startDate: '2021-05',
        endDate: '2024-05',
        location: 'Remote',
        responsibilities: [
            'Developing projects for mobile and Desktop devices',
            'Creating reusable code base for different projects',
            'Leading development teams (up to 4 developers)',
            'Providing technical support to artists and game designers',
            'Writing documentation',
            'Setting up and debugging project builds',
        ],
        achievements: [
            'Released and developed two full-featured mobile projects from scratch',
            'Developed cross-project sub-modules: save system with local storage and NoSQL, localization system, internal config database with Google Sheets integration',
            'Implemented component system with switchable states and external triggers',
        ],
    },
    {
        company: 'Dorfer Games',
        position: 'Unity Developer',
        startDate: '2021-01',
        endDate: '2021-09',
        location: 'Remote',
        responsibilities: [
            'Developing games on Unity engine',
            'Developing game prototypes using internal SDKs',
        ],
        achievements: [
            'Developed 7 hyper-casual game prototypes',
            'Developed procedural environment generators for quick level creation and population',
        ],
    },
];

/**
 * Суммарный стаж. Считается при каждом рендере, поэтому цифры в шапке, в About
 * и в PDF сами обновляются с началом нового месяца.
 */
const TOTAL_EXPERIENCE_MONTHS = totalExperienceMonths(EXPERIENCES);
const TOTAL_EXPERIENCE_YEARS = formatTotalYears(TOTAL_EXPERIENCE_MONTHS);

PROFILE.summary =
    `Software engineer specializing in game development with Unity, with ${TOTAL_EXPERIENCE_YEARS} ` +
    'of commercial experience. Shipped Midcore, Casual and Hyper-Casual titles to mobile, Desktop, ' +
    'console and WebGL platforms. Strong in cross-project architecture, platform porting, Unity ' +
    'Editor tooling and CI/CD, with experience leading small development teams.';

const EDUCATION = [
    {
        school: 'Saint Petersburg National Research University of IT, Mechanics and Optics (ITMO)',
        degree: "Master's Degree",
        field: 'Infocommunication Technologies and Communications Systems',
        year: '2023 — 2026',
    },
    {
        school: 'Saint Petersburg National Research University of IT, Mechanics and Optics (ITMO)',
        degree: "Bachelor's Degree",
        field: 'Infocommunication Technologies and Communications Systems',
        year: '2019 — 2023',
    },
];

/**
 * Навыки сгруппированы: в PDF группы печатаются строками «Категория: a, b, c»
 * (так их разбирают ATS), на сайте раскладываются в плоский список тегов.
 */
const SKILL_GROUPS = [
    {label: 'Game development', items: ['Unity', 'ECS', 'Android SDK']},
    {label: 'Languages', items: ['C#', 'Python', 'Golang', 'SQL']},
    {label: 'Backend', items: ['ASP.NET', 'Web API']},
    {label: 'Engineering practices', items: ['OOP', 'SOLID', 'Git', 'CI/CD', 'Teamwork']},
    {label: 'AI tooling', items: ['Claude Code CLI/Desktop', 'MCP', 'Skills']},
];

const SKILLS = SKILL_GROUPS.reduce((all, group) => all.concat(group.items), []);

const LANGUAGES = [
    {name: 'Russian', level: 'Native'},
    {name: 'English', level: 'B2 — Upper-Intermediate'},
    {name: 'Japanese', level: 'A1 — Beginner'},
    {name: 'Serbian', level: 'A1 — Beginner'},
];

const HOBBIES = [
    {emoji: '📷', name: 'Photography'},
    {emoji: '🌿', name: 'Nature appreciation'},
    {emoji: '🎨', name: 'Drawing'},
    {emoji: '🍳', name: 'Cooking'},
];

// Скачиваемая PDF-версия CV: собирается scripts/generate-cv-pdf.js в public/
const CV_FILE_NAME = 'Vsevolod-Kosarev-Unity-Developer-CV.pdf';
const CV_PATH = `/${CV_FILE_NAME}`;

module.exports = {
    PROFILE,
    CONTACTS,
    EXPERIENCES,
    TOTAL_EXPERIENCE_MONTHS,
    TOTAL_EXPERIENCE_YEARS,
    EDUCATION,
    SKILL_GROUPS,
    SKILLS,
    LANGUAGES,
    HOBBIES,
    CV_FILE_NAME,
    CV_PATH,
};
